import React, { useState, useMemo } from "react";
import { Plus, X } from "lucide-react";

const MONTHS = [
  { id: "jan", label: "Jan" },
  { id: "feb", label: "Feb" },
  { id: "mar", label: "Mar" },
  { id: "apr", label: "Apr" },
  { id: "may", label: "May" },
  { id: "jun", label: "Jun" },
  { id: "jul", label: "Jul" },
  { id: "aug", label: "Aug" },
  { id: "sep", label: "Sep" },
  { id: "oct", label: "Oct" },
  { id: "nov", label: "Nov" },
  { id: "dec", label: "Dec" },
];

// 5 default target rows. Their labels are editable; users can rename them.
// `hasDays: true` adds the inline "Number of days" input (used for Weekends).
export const DEFAULT_TARGET_ROWS = [
  { id: "weekends",      label: "Weekends (Sat & Sun)",                  hasDays: true },
  { id: "sales_calling", label: "Sales Calling" },
  { id: "lunch_kitty",   label: "Lunch Kitty Parties" },
  { id: "promo_cognac",  label: "Promotional Events — Cognac & Cigars" },
  { id: "promo_sushi",   label: "Promotional Events — Sushi & Bubbly" },
];

export const TOTAL_ROWS = [
  { id: "forecasted", label: "Forecasted" },
  { id: "budgeted",   label: "Budgeted" },
  { id: "actual",     label: "Actual" },
];

export const COLS = [
  { id: "covers",  label: "Covers" },
  { id: "apc",     label: "APC" },
  { id: "revenue", label: "Revenue" },
];

export const monthlyTargetCellKey = (month, row, col) =>
  `biz_targets_${month}_${row}_${col}`;
export const monthlyTargetWeekendsDaysKey = (month) =>
  `biz_targets_${month}_weekends_days`;
export const rowLabelKey = (rowId) => `biz_targets_row_label_${rowId}`;
export const CUSTOM_ROWS_KEY = "biz_targets_custom_row_ids";

export function parseCustomRowIds(data) {
  try {
    const v = data?.[CUSTOM_ROWS_KEY];
    if (!v) return [];
    const arr = JSON.parse(v);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string" && /^custom_[a-z0-9]+$/.test(s)) : [];
  } catch {
    return [];
  }
}

// Used by Home.jsx to compute a dynamic total-fields count for the progress bar.
export function getMonthlyTargetKeys(data) {
  const customIds = parseCustomRowIds(data);
  const allRowIds = [
    ...DEFAULT_TARGET_ROWS.map((r) => r.id),
    ...customIds,
    ...TOTAL_ROWS.map((r) => r.id),
  ];
  const cellKeys = MONTHS.flatMap((m) =>
    allRowIds.flatMap((rid) => COLS.map((c) => monthlyTargetCellKey(m.id, rid, c.id)))
  );
  const daysKeys = MONTHS.map((m) => monthlyTargetWeekendsDaysKey(m.id));
  return [...cellKeys, ...daysKeys];
}

function EditableLabel({ value, defaultLabel, locked, onChange, testid }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={locked}
      data-testid={testid}
      placeholder={defaultLabel}
      className="w-full bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-[#D4A968] text-[12px] text-zinc-200 outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-500 placeholder:italic px-0.5 py-0.5"
    />
  );
}

function MonthGrid({
  month,
  data,
  locked,
  onChange,
  customRowIds,
  onRemoveCustomRow,
}) {
  const targetRows = [
    ...DEFAULT_TARGET_ROWS,
    ...customRowIds.map((id) => ({ id, label: "", isCustom: true })),
  ];

  return (
    <div className="overflow-x-auto -mx-1 px-1" data-testid={`month-grid-${month.id}`}>
      <table className="w-full text-xs border-separate border-spacing-0 lg:table-fixed">
        <colgroup>
          <col className="lg:w-[42%]" />
          <col className="lg:w-[19.33%]" />
          <col className="lg:w-[19.33%]" />
          <col className="lg:w-[19.33%]" />
        </colgroup>
        <thead>
          <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <th className="text-left py-1.5 pr-2 sticky left-0 bg-[#141417] z-10 min-w-[180px]">
              Specific Measurable Targets
            </th>
            {COLS.map((c) => (
              <th key={c.id} className="text-left py-1.5 px-1.5 min-w-[90px]">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {targetRows.map((r) => {
            const labelVal = data[rowLabelKey(r.id)] ?? "";
            return (
              <tr key={r.id}>
                <td className="py-1 pr-2 sticky left-0 z-10 bg-[#141417] border-t border-zinc-800/70">
                  <div className="flex items-start gap-1.5">
                    <div className="flex-1 min-w-0">
                      <EditableLabel
                        value={labelVal}
                        defaultLabel={r.label || "New target…"}
                        locked={locked}
                        onChange={(v) => onChange(rowLabelKey(r.id), v)}
                        testid={`row-label-${r.id}`}
                      />
                      {r.hasDays && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">
                            Number of days
                          </span>
                          <input
                            type="text"
                            value={data[monthlyTargetWeekendsDaysKey(month.id)] || ""}
                            onChange={(e) =>
                              onChange(monthlyTargetWeekendsDaysKey(month.id), e.target.value)
                            }
                            disabled={locked}
                            data-testid={`field-${monthlyTargetWeekendsDaysKey(month.id)}`}
                            placeholder="—"
                            className="w-12 bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-1.5 py-0.5 text-[11px] text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-700"
                          />
                        </div>
                      )}
                    </div>
                    {r.isCustom && !locked && (
                      <button
                        type="button"
                        onClick={() => onRemoveCustomRow(r.id)}
                        data-testid={`remove-row-${r.id}`}
                        title="Remove row"
                        className="shrink-0 text-zinc-500 hover:text-[#EF4444] transition-colors p-0.5 -mr-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
                {COLS.map((c) => {
                  const fk = monthlyTargetCellKey(month.id, r.id, c.id);
                  return (
                    <td key={c.id} className="py-1 px-1 border-t border-zinc-800/70">
                      <input
                        type="text"
                        value={data[fk] || ""}
                        onChange={(e) => onChange(fk, e.target.value)}
                        disabled={locked}
                        data-testid={`field-${fk}`}
                        placeholder="—"
                        className="w-full min-w-[80px] bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-2 py-1 text-xs text-white outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-700"
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* Totals — fixed labels, gold accent */}
          {TOTAL_ROWS.map((r) => (
            <tr key={r.id} className="bg-[#0F0F12]">
              <td className="py-1 pr-2 sticky left-0 z-10 bg-[#0F0F12] border-t border-zinc-800/70">
                <div className="text-[12px] text-[#D4A968] font-semibold py-0.5">
                  {r.label}
                </div>
              </td>
              {COLS.map((c) => {
                const fk = monthlyTargetCellKey(month.id, r.id, c.id);
                return (
                  <td key={c.id} className="py-1 px-1 border-t border-zinc-800/70">
                    <input
                      type="text"
                      value={data[fk] || ""}
                      onChange={(e) => onChange(fk, e.target.value)}
                      disabled={locked}
                      data-testid={`field-${fk}`}
                      placeholder="—"
                      className="w-full min-w-[80px] bg-[#0B0B0E] border border-[#D4A968]/30 focus:border-[#D4A968] rounded px-2 py-1 text-xs text-[#FCD34D] outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-700"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MonthlyTargetsCard({ data, locked, onChange }) {
  const [activeMonth, setActiveMonth] = useState(MONTHS[0].id);
  const month = MONTHS.find((m) => m.id === activeMonth) || MONTHS[0];
  const customRowIds = useMemo(() => parseCustomRowIds(data), [data]);

  const setCustomRowIds = (ids) => {
    onChange(CUSTOM_ROWS_KEY, JSON.stringify(ids));
  };

  const onAddRow = () => {
    if (locked) return;
    const newId = `custom_${Date.now().toString(36)}`;
    setCustomRowIds([...customRowIds, newId]);
  };

  const onRemoveCustomRow = (id) => {
    if (locked) return;
    if (!window.confirm("Remove this row? Its data across all 12 months will be deleted.")) return;
    // Also clear that row's persisted values so they don't linger.
    const cleanupKeys = [rowLabelKey(id)];
    MONTHS.forEach((m) => {
      COLS.forEach((c) => cleanupKeys.push(monthlyTargetCellKey(m.id, id, c.id)));
    });
    cleanupKeys.forEach((k) => onChange(k, ""));
    setCustomRowIds(customRowIds.filter((cid) => cid !== id));
  };

  return (
    <div
      data-testid="monthly-targets-card"
      className="bg-[#141417] border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors min-w-0"
    >
      <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-zinc-800">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#D4A968]">
          Focus Revenue per Month
        </div>
        <button
          type="button"
          onClick={onAddRow}
          disabled={locked}
          data-testid="add-target-row-btn"
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-[#D4A968] border border-zinc-700 hover:border-[#D4A968] rounded px-2 py-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Add a custom target row"
        >
          <Plus className="w-3 h-3" />
          Add row
        </button>
      </div>

      {/* Month tab strip */}
      <div
        role="tablist"
        aria-label="Select month"
        data-testid="month-tabs"
        className="flex flex-wrap gap-1 mb-3"
      >
        {MONTHS.map((m) => {
          const isActive = m.id === activeMonth;
          return (
            <button
              key={m.id}
              role="tab"
              aria-selected={isActive}
              data-testid={`month-tab-${m.id}`}
              onClick={() => setActiveMonth(m.id)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded transition-colors ${
                isActive
                  ? "bg-[#D4A968] text-black"
                  : "bg-[#0B0B0E] text-zinc-400 border border-zinc-800 hover:border-[#D4A968]/40 hover:text-zinc-200"
              }`}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <MonthGrid
        month={month}
        data={data}
        locked={locked}
        onChange={onChange}
        customRowIds={customRowIds}
        onRemoveCustomRow={onRemoveCustomRow}
      />
    </div>
  );
}
